import { Select } from '@napaicp/ui'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useSession } from '@/features/session/use-session'
import { homeForCompetition } from '@/lib/role-routing'
import { useCompetitions } from './api'

/**
 * Switches the competition the current screen is scoped to.
 *
 * Changing it navigates rather than mutating a shared store: the competition id
 * lives in the URL (or query string, for the judge routes), so every query key
 * derived from it changes with the route and no screen can end up rendering
 * one competition's data under another's heading.
 *
 * The destination depends on the role the caller holds *in the competition
 * just selected* — `homeForCompetition` is the same map the `/` landing
 * redirect uses, so a student or judge picking a competition here never lands
 * in `/admin/*`, which they may not even be able to render (see UX-003/UX-005
 * in the acceptance report).
 */
export function CompetitionSwitcher() {
  const { t } = useTranslation('nav')
  const navigate = useNavigate()
  const params = useParams<{ competitionId?: string }>()
  const competitions = useCompetitions()
  const session = useSession()

  if (competitions.data === undefined || competitions.data.length === 0) {
    return null
  }

  const globalRole = session.data?.user?.globalRole ?? 'user'

  return (
    <Select
      aria-label={t('competition')}
      className="w-full min-w-0 sm:w-56"
      value={params.competitionId ?? ''}
      onChange={(event) => {
        const nextId = event.target.value
        if (nextId.length === 0) {
          return
        }
        const competition = competitions.data.find((entry) => entry.id === nextId)
        const roles = competition?.roles ?? []
        void navigate(homeForCompetition(globalRole, roles, nextId))
      }}
    >
      <option value="">{t('selectCompetition')}</option>
      {competitions.data.map((competition) => {
        const isSelected = params.competitionId === competition.id
        const label = `${competition.name} (${competition.year})`
        return (
          <option key={competition.id} value={competition.id}>
            {isSelected ? t('currentCompetition', { name: label }) : label}
          </option>
        )
      })}
    </Select>
  )
}
